// ** React Imports
import { Fragment, lazy, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
// ** Layouts
import BlankLayout from "@layouts/BlankLayout";
import VerticalLayout from "@src/layouts/VerticalLayout";
import HorizontalLayout from "@src/layouts/HorizontalLayout";
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper";

// ** Route Components
import PublicRoute from "@components/routes/PublicRoute";
import { isObjEmpty } from "@src/utility/Utils";
import { RouterType } from "@src/@types/RouterTypes";
import { useDispatch, useSelector } from "react-redux";
import { handleSetActiveDomain } from "@src/redux/reducers/userAccount";
import { AccountFeatures } from "@src/redux/reducers/accountFeatures";
import { RootState } from "@src/redux";
import { PlanType, Subscription } from "@src/@types/UserAccount";


// ** Utils

const getLayout: { [key: string]: any } = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
  horizontal: <HorizontalLayout />,
};

// ** Document title
const TemplateTitle = "%s - Vuexy React Admin Template";

// ** Default Route
const DefaultRoute = "/dashboard";

const Installation = lazy(() => import("../../views/installation/Installation"));
const Perfil = lazy(() => import("../../views/perfil/PefilEdit"));
const Sites = lazy(() => import("../../views/sites/Sites"));
const Login = lazy(() => import("../../views/login/Login"));
const Register = lazy(() => import("../../views/register/Register"));
const PlanChoose = lazy(() => import("@src/views/plans/PlanChoose"));
const BillingSuccess = lazy(() => import("@src/views/billing/BillingSuccess"));
const BillingCancel = lazy(() => import("@src/views/billing/BillingCancel"));
const BillingExpired = lazy(() => import("@src/views/billing/BillingExpired"));
const AccountConfig = lazy(() => import("@src/views/account/AccountConfig"));
const ForgotPassword = lazy(
  () => import("../../views/forgotPassword/ForgotPassword")
);
const Error = lazy(() => import("../../views/Error"));
const ErrorLogout = lazy(() => import("../../views/ErrorLogout"));

const DomainDashboard = lazy(() => import('../../views/dashboard/Dashboard'))
const AlternativeTexts = lazy(() => import('../../views/ai-content/alternative-texts/AlternativeTexts'))
const SocialImages = lazy(() => import('../../views/ai-content/social-images/SocialImages'))
const AiContentReview = lazy(() => import('../../views/ai-content/review/AiContentReview'))

// ==========================================
// COMPAT: URLs antigas com :domain (ex: bookmarks)
// Aplica o domínio como ativo e redireciona pra rota limpa.
// ==========================================
const LegacyDomainRedirect = ({ to }: { to: string }) => {
  const dispatch = useDispatch();
  const { domain } = useParams<{ domain?: string }>();
  useEffect(() => {
    if (domain) dispatch(handleSetActiveDomain(domain));
  }, [domain, dispatch]);
  return <Navigate replace to={to} />;
}

const AlternativeTextsGuarded = () => {
  const { hideAlternativeTexts } = useSelector<RootState, AccountFeatures>(
    (state) => state.accountFeatures
  );
  if (hideAlternativeTexts) return <Navigate replace to={DefaultRoute} />;
  return <AlternativeTexts />;
}

const withEnterpriseBlock = (Component: React.ComponentType) => () => {
  const subscription = useSelector<RootState, Subscription>(
    (state) => state.userAccount.subscription as Subscription
  );
  if (subscription?.planType === PlanType.ENTERPRISE) return <Navigate replace to={DefaultRoute} />;
  return <Component />;
}

const PlanChooseGuarded = withEnterpriseBlock(PlanChoose);
const BillingSuccessGuarded = withEnterpriseBlock(BillingSuccess);
const BillingCancelGuarded = withEnterpriseBlock(BillingCancel);
const BillingExpiredGuarded = withEnterpriseBlock(BillingExpired);
const AccountConfigGuarded = withEnterpriseBlock(AccountConfig);

// ** Merge Routes
const Routes = [
  {
    path: "/",
    index: true,
    element: <Navigate replace to={DefaultRoute} />,
  },
  {
    path: "/index.html",
    element: <Navigate replace to={DefaultRoute} />,
    logged: true,
  },
  {
    path: '/dashboard',
    element: <DomainDashboard />,
    logged: true
  },
  {
    path: '/dashboard/:domain',
    element: <LegacyDomainRedirect to="/dashboard" />,
    logged: true,
    hasParams: true
  },
  {
    path: "/sites",
    element: <Sites />,
    logged: true,
  },
  {
    path: "/plan",
    element: <PlanChooseGuarded />,
    logged: true,
  },
  {
    path: "/billing/success",
    element: <BillingSuccessGuarded />,
    logged: true,
  },
  {
    path: "/billing/cancel",
    element: <BillingCancelGuarded />,
    logged: true,
  },
  {
    path: "/billing/expired",
    element: <BillingExpiredGuarded />,
    logged: true,
  },
  {
    path: "/installation",
    element: <Installation />,
    logged: true,
  },
  {
    path: "/ai-content/alternative-texts",
    element: <AlternativeTextsGuarded />,
    logged: true,
  },
  {
    path: "/ai-content/alternative-texts/:domain",
    element: <LegacyDomainRedirect to="/ai-content/alternative-texts" />,
    logged: true,
    hasParams: true,
  },
  {
    path: "/ai-content/social-images",
    element: <SocialImages />,
    logged: true,
  },
  {
    path: "/ai-content/social-images/:domain",
    element: <LegacyDomainRedirect to="/ai-content/social-images" />,
    logged: true,
    hasParams: true,
  },
  {
    path: "/ai-content/review",
    element: <AiContentReview />,
    logged: true,
  },
  {
    path: "/perfil",
    element: <Perfil />,
    logged: true,
  },
  {
    path: "/account-settings",
    element: <AccountConfigGuarded />,
    logged: true,
  },
  {
    path: "/login",
    element: <Login />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/register",
    element: <Register />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "forgot-password/:emailHash",
    hasParams: true,
    element: <Register />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/error",
    element: <Error />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/errorLogout/:errorCode",
    hasParams: true,
    element: <ErrorLogout />,
    meta: {
      layout: "blank",
    },
  },
];

const getRouteMeta = (route: any) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta };
    } else {
      return {};
    }
  }
};



// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout: string, defaultLayout: string) => {
  const LayoutRoutes: Array<any> = [];

  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false;
      //let route = deepCopy(routeLocal);
      // ** Checks if Route layout or Default layout matches current layout
      if (
        (route.meta && route.meta.layout && route.meta.layout === layout) ||
        ((route.meta === undefined || route.meta.layout === undefined) &&
          defaultLayout === layout)
      ) {
        const RouteTag = PublicRoute;

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === "blank" ? (isBlank = true) : (isBlank = false);
        }
        if (route.element) {
          const Wrapper =
            // eslint-disable-next-line multiline-ternary
            isObjEmpty(route.element.props) && isBlank === false
              ? // eslint-disable-next-line multiline-ternary
              LayoutWrapper
              : Fragment;

          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          );
        }

        // Push route to LayoutRoutes
        LayoutRoutes.push(route);
      }
      return LayoutRoutes;
    });
  }
  return LayoutRoutes;
};

const getRoutes = (layout: string) => {
  const defaultLayout = layout || "vertical";
  const layouts: Array<string> = ["vertical", "horizontal", "blank"];

  const AllRoutes: Array<RouterType> = [];

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout);

    AllRoutes.push({
      path: "/",
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes,
    });
  });

  return AllRoutes;
};

export { DefaultRoute, TemplateTitle, Routes, getRoutes };
